<script setup lang="ts">
/**
 * Account menu opened from the avatar of the top bar.
 *
 * Small non modal popover: Escape closes it, a click outside closes it, and the
 * focus moves to the first entry when it opens so it stays reachable from the
 * keyboard alone. It carries the sign out action, which the corpus requires to
 * be available from every screen.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  ouvert: boolean;
  /** Full name of the signed in advisor. */
  nom: string;
  /** French label of the role, already resolved by the caller. */
  role: string;
}>();

const emit = defineEmits<{
  fermer: [];
  deconnexion: [];
}>();

const racine = ref<HTMLElement | null>(null);
const premiereEntree = ref<HTMLButtonElement | null>(null);

function surClicDocument(evenement: MouseEvent): void {
  if (!props.ouvert) {
    return;
  }
  const cible = evenement.target;
  if (cible instanceof Node && racine.value?.contains(cible) === true) {
    return;
  }
  emit('fermer');
}

function surTouche(evenement: KeyboardEvent): void {
  if (props.ouvert && evenement.key === 'Escape') {
    emit('fermer');
  }
}

watch(
  () => props.ouvert,
  async (ouvert) => {
    if (!ouvert) {
      return;
    }
    await nextTick();
    premiereEntree.value?.focus();
  },
);

onMounted(() => {
  document.addEventListener('mousedown', surClicDocument);
  document.addEventListener('keydown', surTouche);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', surClicDocument);
  document.removeEventListener('keydown', surTouche);
});
</script>

<template>
  <div
    v-if="props.ouvert"
    ref="racine"
    class="menu"
    role="menu"
    aria-label="Menu du compte"
  >
    <p class="menu__nom">
      {{ props.nom }}
    </p>
    <p class="menu__role">
      {{ props.role }}
    </p>
    <button
      ref="premiereEntree"
      type="button"
      class="menu__entree"
      role="menuitem"
      @click="emit('deconnexion')"
    >
      Se déconnecter
    </button>
  </div>
</template>

<style scoped>
.menu {
  position: absolute;
  top: calc(var(--hz-topbar-h) - var(--hz-sp-2));
  right: var(--hz-sp-5);
  z-index: 40;
  min-width: 208px;
  padding: var(--hz-sp-3);
  border: 1px solid var(--hz-border);
  border-radius: var(--hz-radius-card);
  background: var(--hz-bg-card);
  box-shadow: var(--hz-shadow-pop);
  font-family: var(--hz-font);
}

.menu__nom {
  font-size: var(--hz-fs-md);
  font-weight: 700;
  color: var(--hz-text-strong);
}

.menu__role {
  margin-top: var(--hz-sp-1);
  font-size: var(--hz-fs-sm);
  color: var(--hz-text-muted);
}

.menu__entree {
  width: 100%;
  height: 36px;
  margin-top: var(--hz-sp-3);
  padding: 0 var(--hz-sp-3);
  border: 1px solid var(--hz-border);
  border-radius: var(--hz-radius);
  background: var(--hz-bg-card);
  color: var(--hz-text-strong);
  font-family: inherit;
  font-size: var(--hz-fs-md);
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.menu__entree:hover {
  background: var(--hz-bg-app);
}

@media (max-width: 479px) {
  .menu {
    right: var(--hz-sp-3);
  }
}
</style>
